import { FC } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Container, MenuItem, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ArrowBack } from '@mui/icons-material';
import { QuestionnaireResult, QuestionnaireResultsFilter, SittingStatus } from '@stores/QuestionnaireAdminStore';
import { ResultDialog } from '@app/components/Admin/Questionnaires/ResultDialog';
import { ScoreSummary } from '@app/components/Admin/Questionnaires/ScoreSummary';
import {
  sittingStatusColors,
  sittingStatusLabels,
} from '@app/components/Admin/Questionnaires/questionnaireResultsFormatting';
import { formatPublishedAt } from './AdminQuestionnaires';
import { useQuestionnaireResultsController } from './useQuestionnaireResultsController';

const columns: GridColDef<QuestionnaireResult>[] = [
  { field: 'cohortUserId', headerName: 'Participant ID', width: 130 },
  { field: 'cohortName', headerName: 'Cohort', flex: 1, minWidth: 150 },
  {
    field: 'formName',
    headerName: 'Questionnaire',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value, row) => (row.formVersion ? `${row.formName} v${row.formVersion}` : row.formName),
  },
  {
    field: 'timepointCode',
    headerName: 'Timepoint',
    width: 120,
    valueFormatter: (value: string | null) => value ?? 'Ad hoc',
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (params) => (
      <Chip
        size="small"
        label={sittingStatusLabels[params.value as SittingStatus]}
        color={sittingStatusColors[params.value as SittingStatus]}
      />
    ),
  },
  {
    field: 'availableAt',
    headerName: 'Opens',
    width: 170,
    valueFormatter: (value: string) => formatPublishedAt(value),
  },
  {
    field: 'expiresAt',
    headerName: 'Closes',
    width: 170,
    valueFormatter: (value: string | null) => (value ? formatPublishedAt(value) : 'Never'),
  },
  {
    field: 'completedAt',
    headerName: 'Completed',
    width: 170,
    valueFormatter: (value: string | null) => formatPublishedAt(value),
  },
  {
    field: 'scores',
    headerName: 'Scores',
    flex: 2,
    minWidth: 280,
    sortable: false,
    renderCell: (params) => <ScoreSummary scores={params.row.scores} />,
  },
];

/** Every participant's sittings for the chosen cohort, questionnaire or timepoint, with status and scores. */
export const AdminQuestionnaireResults: FC = () => {
  const resultsPage = useQuestionnaireResultsController();

  const filterSelect = (
    key: keyof QuestionnaireResultsFilter,
    label: string,
    options: { id: number; name: string }[]
  ) => (
    <TextField
      select
      label={label}
      value={resultsPage.filter[key] ?? ''}
      onChange={(e) => resultsPage.handleFilterChange(key, String(e.target.value))}
      size="small"
      sx={{ minWidth: 200 }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );

  if (resultsPage.optionsLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="section" maxWidth="xl" sx={{ py: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={resultsPage.handleBack} sx={{ mb: 1 }}>
        Back to Questionnaires
      </Button>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each row is one participant&apos;s questionnaire at one timepoint. Participants are shown by ID, not name, to
        keep results de-identified; the ID stays the same across a participant&apos;s timepoints on a team. Missed means
        it closed before they finished it. Click a row to see their answers.
      </Typography>

      {resultsPage.optionsFailed ? (
        <Alert severity="error">The filters could not be loaded.</Alert>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          {filterSelect('cohortId', 'Cohort', resultsPage.cohorts)}
          {filterSelect('formId', 'Questionnaire', resultsPage.forms)}
          {filterSelect('timepointId', 'Timepoint', resultsPage.timepoints)}
          <Button onClick={resultsPage.handleClearFilters} disabled={!resultsPage.filtered}>
            Clear
          </Button>
        </Box>
      )}

      {!resultsPage.filtered ? (
        <Alert severity="info">Choose a cohort, questionnaire or timepoint to see results.</Alert>
      ) : resultsPage.resultsFailed ? (
        <Alert severity="error">The results could not be loaded.</Alert>
      ) : (
        <DataGrid
          rows={resultsPage.results}
          columns={columns}
          getRowId={(row) => row.sittingId}
          loading={resultsPage.resultsLoading}
          density="compact"
          onRowClick={(params) => resultsPage.handleViewResult(params.row.sittingId)}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          // Scores take two lines, so rows grow to fit them.
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 1 },
          }}
        />
      )}

      <ResultDialog sittingId={resultsPage.viewingSittingId} onClose={resultsPage.handleCloseResult} />
    </Container>
  );
};
